import { callable } from '../callable';

export type TenantRole =
  | 'tenantAdmin'
  | 'registrar'
  | 'faculty'
  | 'reviewer'
  | 'mentor'
  | 'finance'
  | 'student'
  | 'donor';

export interface MembershipRecord {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  roles: TenantRole[];
  status: 'active' | 'suspended' | 'pending';
  personId?: string;
  personName?: string;
  joinedAt?: string;
}

export interface InviteUserRequest {
  tenantId: string;
  email: string;
  /** Preferred: use firstName + lastName for consistency with Person records */
  firstName?: string;
  lastName?: string;
  /** Legacy fallback if firstName/lastName not provided */
  displayName?: string;
  roles: TenantRole[];
  /** Institutional position(s) of the person in the organization (stored in Person.institutionalRoles) */
  institutionalRoles?: string[];
  personId?: string;
}

export interface UpdateRolesRequest {
  tenantId: string;
  uid: string;
  roles: TenantRole[];
  /** Institutional position(s) — updated on the linked Person record */
  institutionalRoles?: string[];
}

export interface UpdatePersonLinkRequest {
  tenantId: string;
  uid: string;
  personId: string | null;
}

export interface RemoveMemberRequest {
  tenantId: string;
  uid: string;
}

export interface ResendInviteRequest {
  tenantId: string;
  uid: string;
}

export type InviteTokenStatus = 'valid' | 'not-found' | 'expired' | 'already-used' | 'superseded';

export const usersApi = {
  invite: async (data: InviteUserRequest) => {
    const fn = callable<InviteUserRequest, { uid: string; email: string; roles: TenantRole[] }>(
      'inviteUser',
    );
    const result = await fn(data);
    return result.data;
  },

  updateRoles: async (data: UpdateRolesRequest) => {
    const fn = callable<UpdateRolesRequest, { uid: string; roles: TenantRole[] }>(
      'updateMembershipRoles',
    );
    const result = await fn(data);
    return result.data;
  },

  updatePersonLink: async (data: UpdatePersonLinkRequest) => {
    const fn = callable<UpdatePersonLinkRequest, { uid: string; personId: string | null }>(
      'updateMembershipPersonLink',
    );
    const result = await fn(data);
    return result.data;
  },

  remove: async (data: RemoveMemberRequest) => {
    const fn = callable<RemoveMemberRequest, { success: boolean }>('removeMember');
    const result = await fn(data);
    return result.data;
  },

  resendInvite: async (data: ResendInviteRequest) => {
    const fn = callable<ResendInviteRequest, { success: boolean }>('resendInvite');
    const result = await fn(data);
    return result.data;
  },

  redeemInviteToken: async (token: string) => {
    const fn = callable<
      { token: string },
      {
        oobCode: string;
        email: string;
        tenantId: string;
        branding?: { tenantName: string; primaryColor: string };
      }
    >('redeemInviteToken');
    const result = await fn({ token });
    return result.data;
  },

  /** Public — ¿sirve este enlace? Solo LEE: abrir la página no lo gasta.
   *  `email` y `tenantId` vienen solo si el estado es `valid`. */
  checkInviteToken: async (token: string) => {
    const fn = callable<
      { token: string },
      {
        status: InviteTokenStatus;
        reason?: string;
        email?: string;
        tenantId?: string;
        branding: { tenantName: string; primaryColor: string };
        /** El idioma del seminario; ausente en un servidor anterior. */
        language?: { locale: string | null; isNetwork: boolean; credentialRecord: boolean };
      }
    >('checkInviteToken');
    const result = await fn({ token });
    return result.data;
  },

  /** Public — fija la contraseña y recién ahí gasta el enlace (en el servidor,
   *  en una transacción). Si algo falla, el enlace sigue sirviendo. */
  completeInviteActivation: async (token: string, password: string) => {
    const fn = callable<{ token: string; password: string }, { email: string; tenantId: string }>(
      'completeInviteActivation',
    );
    const result = await fn({ token, password });
    return result.data;
  },

  /** Public — fetches tenant branding for a given invite token WITHOUT redeeming it.
   *  Returns fallback branding when the token is expired, used, or not found. */
  resolveInviteTokenBranding: async (token: string) => {
    const fn = callable<{ token: string }, { tenantName: string; primaryColor: string }>(
      'resolveInviteTokenBranding',
    );
    const result = await fn({ token });
    return result.data;
  },

  /** Sends a password-reset / re-activation email via Resend (backend Cloud Function).
   *  Always returns { success: true } regardless of whether the email is registered. */
  requestPasswordReset: async (email: string) => {
    const fn = callable<{ email: string }, { success: boolean }>('requestPasswordReset');
    const result = await fn({ email });
    return result.data;
  },

  /** Public — fetches tenant branding given a tenantId string.
   *  Used when the activation page receives a tenantId param from the password-reset URL. */
  getBrandingByTenantId: async (tenantId: string) => {
    const fn = callable<{ tenantId: string }, { tenantName: string; primaryColor: string }>(
      'getBrandingByTenantId',
    );
    const result = await fn({ tenantId });
    return result.data;
  },
};
